import { decamelizeKeys } from "humps";
import trim from "lodash/trim";
import { RoleWatchersResponse } from "PFApp/activities/types/watchers";
import { TemplateKey } from "PFApp/constants/templates";
import api from "PFCore/api";
import { RequestConfig } from "PFCore/api/api";
import { decamelizeDesiredKeys } from "PFCore/helpers/keys_camelization_utils";
import {
  Activity,
  ActivityData,
  ActivityOption,
  Collection,
  Id,
  OrderOptionOrEmpty,
  PaginatedCollection,
  PostUntilChangeReasonData,
  QueryParams
} from "PFTypes";
import { ActivityFeedback, ActivityFeedbackCreateData, FeedbackForProfile } from "PFTypes/activity_feedback";
import { ActivityParticipant } from "PFTypes/activity_participant";
import { RequireAtLeastOne } from "type-fest";

export type ActivityResponseData = { id: Id; activity: Activity };

export type ActivitiesResponse = PaginatedCollection<ActivityResponseData[]>;

// TODO: [PROF-3813] params should be typed when filters types are fixed
export const fetchActivities = (
  params?: QueryParams,
  headers?: any,
  config: RequestConfig = {}
): Promise<ActivitiesResponse> =>
  api({
    url: "activities",
    method: "GET",
    params,
    headers,
    ...config
  }).then((res) => decamelizeDesiredKeys<ActivitiesResponse>(res, ["entries"])); // TODO: [PROF-5746] Remove decamelizeKeys when app is camelized

export const fetchActivity = (activityId: Id): Promise<Activity> =>
  api({
    url: `activities/${activityId}`,
    method: "GET"
  }).then((res) => decamelizeKeys(res) as Activity); // TODO: [PROF-5746] Remove decamelizeKeys when app is camelized

export type CloseActivityPayload = {
  activityId: number;
  vacanciesClosureReason?: string | null;
};

export const closeActivity = ({
  activityId,
  vacanciesClosureReason
}: CloseActivityPayload): Promise<Activity> =>
  api({
    url: `activities/${activityId}/close`,
    method: "PUT",
    data: {
      ...(vacanciesClosureReason ? { vacanciesClosureReason } : {})
    }
  }).then((res) => decamelizeKeys(res) as Activity); // TODO: [PROF-5746] Remove decamelizeKeys when app is camelized

export type BulkCloseActivitiesPayload = {
  activityIds: number[];
};

export const bulkCloseActivities = ({ activityIds }: BulkCloseActivitiesPayload): Promise<void> =>
  api({
    url: "activities/bulk_close",
    method: "POST",
    data: {
      activityIds
    }
  });

export type ReopenActivityPayload = {
  activityId: number;
  postUntil: string;
  postUntilChangeReason?: PostUntilChangeReasonData;
};

export const reopenActivity = ({ activityId, ...data }: ReopenActivityPayload): Promise<void> =>
  api({
    url: `activities/${activityId}/reopen`,
    method: "PUT",
    data
  });

export const updateActivity = (activityId: Id, data: Partial<ActivityData>): Promise<Activity> =>
  api({
    url: `activities/${activityId}`,
    method: "PUT",
    data
  }).then((res) => decamelizeKeys(res) as Activity); // TODO: [PROF-5746] Remove decamelizeKeys when app is camelized

export const createActivity = (data: ActivityData): Promise<Activity> =>
  api({
    url: "activities",
    method: "POST",
    data
  }).then((resp) => decamelizeKeys(resp) as Activity); // TODO: [PROF-5746] Remove decamelizeKeys when app is camelized

export const deleteActivity = (activityId: Id): Promise<void> =>
  api({
    url: `activities/${activityId}`,
    method: "DELETE"
  });

export const forceManual = (activityId: number) =>
  api({
    url: `audit_roles/${activityId}/force_manual`,
    method: "PUT"
  });

export const fetchActivityWatchers = (
  activityId: Id,
  page = 1,
  perPage = 10,
  filters = {},
  order?: OrderOptionOrEmpty
): Promise<RoleWatchersResponse> =>
  api({
    url: `activities/${activityId}/interests`,
    params: {
      perPage,
      page,
      filters,
      order
    }
  });

export type QuestionnaireAnswers = {
  answer1?: boolean | null;
  answer2?: boolean | null;
  answer3?: boolean | null;
  statement?: string | null;
};

export type ShowInterestResponse = QuestionnaireAnswers & {
  id: number;
  activity_id: number;
  profile_id: number;
  createdAt: string;
};

export const showInterest = (
  activityId: Id,
  data: QuestionnaireAnswers = {}
): Promise<ShowInterestResponse> =>
  api({
    url: `activities/${activityId}/interests`,
    method: "POST",
    data
  });

export const removeInterest = (activityId: Id, interestId: Id): Promise<void> =>
  api({
    url: `activities/${activityId}/interests/${interestId}`,
    method: "DELETE"
  });

export const fetchActivityFeedbackParticipants = (
  activityId: Id
): Promise<Collection<ActivityParticipant[]>> =>
  api({
    url: `activities/${activityId}/feedback_participants`,
    method: "GET"
  });

export const fetchActivityFeedback = (feedbackId: Id): Promise<ActivityFeedback> =>
  api({
    url: `feedback/${feedbackId}`,
    method: "GET"
  });

export const fetchProfileFeedback = (
  profileId: Id,
  rolesIds: Id[]
): Promise<Collection<FeedbackForProfile[]>> =>
  api({
    url: "feedback",
    params: { profileId, rolesIds }
  });

export const sendActivityFeedback = (data: ActivityFeedbackCreateData): Promise<void> =>
  api({
    url: "feedback",
    method: "POST",
    data
  });

export type ActivitiesOptionsParams = {
  templateKey?: TemplateKey | TemplateKey[];
  perPage?: number;
  owned?: boolean;
  contributor?: boolean;
  state?: string[];
  activitiesWithNotifications?: boolean;
  matchedProfileId?: Id;
} & RequireAtLeastOne<{
  term: string;
  id: Id[];
  parentActivityId: Id;
}>;

export type ActivityOptionsResponse = Collection<ActivityOption[]>;

export const fetchActivitiesOptions = ({
  id,
  term,
  templateKey,
  perPage,
  owned,
  contributor,
  state,
  activitiesWithNotifications,
  parentActivityId,
  matchedProfileId
}: ActivitiesOptionsParams) => {
  const trimmedTerm = trim(term);

  return api<ActivityOptionsResponse>({
    url: "options/activities",
    params: {
      id,
      ...(trimmedTerm ? { term: trimmedTerm } : {}),
      templateKey,
      perPage,
      owned,
      contributor,
      state,
      activitiesWithNotifications,
      parentActivityId,
      matchedProfileId
    }
  });
};
